import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, Lock } from 'lucide-react';
import './PrivacyPolicy.css';

const SECTIONS = [
  {
    title: 'What We Collect',
    body: `When you sign in as an organizer, we collect your Google account email address and display name via Firebase Authentication. We do not store your Google password or any payment credentials.\n\nWhen members submit expenses, we collect: the name you enter, receipt photos you upload, item descriptions and amounts extracted from those photos, and the optional payment handle you provide (e.g., Venmo username, Cash App tag). We also collect basic usage counters (page view counts, not tied to individuals).`,
  },
  {
    title: 'How We Use It',
    body: `All data collected is used solely to provide the XpenseMate group expense splitting service — to display your event to group members, calculate settlements, and generate payment links. We do not use your data for advertising, profiling, or any purpose beyond operating the app.`,
  },
  {
    title: 'Receipt Photos',
    body: `Receipt images you upload are stored in Google Firebase Cloud Storage under your event code. They are used only to extract line item data via Google Cloud Vision OCR. Images are automatically deleted after 30 days. You should avoid including sensitive information beyond what is needed to split expenses.`,
  },
  {
    title: 'Payment Handles',
    body: `If you provide a Venmo, Cash App, PayPal, or other payment handle, it is stored in Firestore and displayed to the event organizer to generate payment links. It is not transmitted to any payment service by XpenseMate — it is used only to construct a deep link that you can choose to tap. XpenseMate never initiates or processes payments on your behalf.`,
  },
  {
    title: 'Third Parties',
    body: `XpenseMate is built on Google Firebase (Authentication, Firestore, Cloud Storage, Cloud Functions) and Google Cloud Vision API. Your data is subject to Google's privacy policies when stored or processed on those platforms. We do not share your data with any other third party. Payment service deep links (Venmo, Cash App, PayPal, etc.) are generated client-side — no data is sent to those services by XpenseMate.`,
  },
  {
    title: 'Data Retention',
    body: `Organizer accounts and event data persist until you request deletion. Receipt images are deleted automatically after 30 days. Member submission data (name, items, amounts) is stored as part of the event and persists as long as the event exists. To request deletion of your account or any event data, email us at mohapatra.shyam@gmail.com.`,
  },
  {
    title: 'Cookies and Tracking',
    body: `XpenseMate does not use advertising cookies or third-party tracking scripts. Firebase Authentication uses a session token stored in your browser's local storage to keep you signed in. We store a temporary session flag (sessionStorage) to track whether you've initiated a payment in the current browser tab — this is cleared when you close the tab and never sent to a server.`,
  },
  {
    title: 'Children',
    body: `XpenseMate is not directed at children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has submitted information through XpenseMate, contact us at mohapatra.shyam@gmail.com and we will delete it promptly.`,
  },
  {
    title: 'Your Rights',
    body: `You may request access to, correction of, or deletion of your personal data at any time by emailing mohapatra.shyam@gmail.com. For organizer accounts, you can also delete your events directly from the dashboard. We will respond to data requests within 30 days. If you are in the European Economic Area or California, you have additional rights under GDPR and CCPA respectively — these are honored upon request.`,
  },
  {
    title: 'Security',
    body: `All data is transmitted over HTTPS. Firebase security rules restrict access so that only event organizers can read or modify their own events, and only authenticated users can write to protected collections. Receipt images in Cloud Storage are not publicly accessible by URL. No financial account numbers or payment credentials are stored by XpenseMate at any time.`,
  },
  {
    title: 'Changes to This Policy',
    body: `We may update this Privacy Policy as the app evolves. Material changes will be noted by updating the "Last updated" date below. Continued use of XpenseMate after changes constitutes acceptance of the updated policy. For significant changes we will make reasonable efforts to notify organizers via the email associated with their account.`,
  },
];

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="pp-page">
      <header className="pp-header">
        <button className="pp-back" onClick={() => navigate(-1)}>← Back</button>
        <div className="pp-logo">
          <div className="pp-logo-icon"><Receipt size={16} color="white" /></div>
          <span className="pp-logo-text">XpenseMate</span>
        </div>
      </header>

      <main className="pp-main">
        <div className="pp-hero">
          <div className="pp-shield">
            <Lock size={28} />
          </div>
          <h1 className="pp-title">Privacy Policy</h1>
          <p className="pp-subtitle">
            Last updated: April 2026.
          </p>
        </div>

        <div className="pp-notice">
          <strong>Summary:</strong> XpenseMate collects only what it needs to split expenses — your Google sign-in, event details, receipt photos, and optional payment handles. We don't sell data, don't run ads, and don't share anything with third parties beyond Google Firebase infrastructure. Receipt photos are auto-deleted after 30 days.
        </div>

        <div className="pp-sections">
          {SECTIONS.map((s, i) => (
            <div key={i} className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-num">{i + 1}</span>
                {s.title}
              </h2>
              {s.body.split('\n\n').map((para, j) => (
                <p key={j} className="pp-section-body">{para}</p>
              ))}
            </div>
          ))}
        </div>

        <div className="pp-footer-note">
          <p>
            Questions or data requests? Email{' '}
            <a href="mailto:mohapatra.shyam@gmail.com" className="pp-link">
              mohapatra.shyam@gmail.com
            </a>
          </p>
          <p className="pp-copyright">© 2026 XpenseMate. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
}
